import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * useMediaRecorder — browser MediaRecorder wrapper for Telegram /
 * iMessage-style voice messages.
 *
 * Why a custom hook (vs reaching for a library):
 *   - We need feature-detection across the messy Safari/Firefox/Chrome
 *     MIME-support landscape (webm/opus on most; mp4/aac on iOS Safari).
 *   - The AudioContext for the level meter MUST be created inside the
 *     user-gesture handler that calls `start()` — instantiating on
 *     mount gives a suspended context that never resumes on iOS Safari
 *     and the level bars stay flat.
 *   - We need a 25-second hard cap that fires a callback before the
 *     server-side cap (worker/routes/eric-voice.ts MAX_DURATION_MS) rejects.
 *   - We need `track.onended` to detect mid-record permission
 *     revocation (user opens Safari Settings, kills mic, returns to
 *     tab) — the recorder otherwise silently produces zeros.
 *
 * The hook does NOT handle the upload or the gesture state machine —
 * those live in VoiceComposer. This is just the audio plumbing.
 */

// Browser-negotiated MIME types. Order matters: we prefer Opus
// (Chrome/Firefox) for its size/quality at low bitrates; fall back to
// MP4/AAC on iOS Safari which doesn't support webm/opus recording.
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
] as const

export const MAX_RECORDING_MS = 25_000
// Below this the audio is too short to transcribe usefully and almost
// certainly an accidental tap. VoiceComposer turns these into a "Hold
// to record" toast rather than uploading garbage.
export const MIN_RECORDING_MS = 750

export type MediaRecorderSupport = 'supported' | 'unsupported'

export type RecorderStatus =
  | 'idle'
  | 'requesting_permission'
  | 'recording'
  | 'stopping'
  | 'error'

export type StopReason = 'user' | 'cancel' | 'max_length' | 'permission_revoked'

export type StoppedClip = {
  blob: Blob
  mimeType: string
  durationMs: number
  reason: StopReason
}

export type UseMediaRecorderOptions = {
  /**
   * Called when stop() resolves with a usable clip (duration >= the
   * passed-in minimum, blob non-empty, not cancelled).
   */
  onClip?: (clip: StoppedClip) => void
  /**
   * Called when start() or stop() encounters an unrecoverable error
   * (permission denied, no usable codec, MediaRecorder threw mid-clip).
   */
  onError?: (error: Error) => void
}

export type UseMediaRecorderReturn = {
  support: MediaRecorderSupport
  status: RecorderStatus
  /**
   * Live-updating peak amplitude in [0, 1] from the analyser node.
   * VoiceComposer renders this as the level meter. Reset to 0 when
   * not recording.
   */
  level: number
  /**
   * Ms since start() resolved. Updates ~10x/sec while recording.
   * Stable at 0 when not recording.
   */
  durationMs: number
  start: () => Promise<void>
  stop: () => void
  cancel: () => void
}

function pickSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  for (const mime of PREFERRED_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) return mime
    } catch {
      // Some embedded webviews throw on isTypeSupported — treat as no
      // support and continue.
    }
  }
  return null
}

export function useMediaRecorder(
  options: UseMediaRecorderOptions = {},
): UseMediaRecorderReturn {
  const [status, setStatus] = useState<RecorderStatus>('idle')
  const [level, setLevel] = useState(0)
  const [durationMs, setDurationMs] = useState(0)

  // Compute support once via lazy initializer. We don't dynamically
  // re-check because the underlying capability doesn't change at
  // runtime — feature detection on first render is sufficient and
  // keeps the renderer pure (refs written from render bodies trip the
  // react-hooks/purity rule, hence useState here).
  const [support] = useState<MediaRecorderSupport>(() => {
    if (typeof window === 'undefined') return 'supported'
    if (
      typeof MediaRecorder === 'undefined' ||
      !navigator.mediaDevices ||
      typeof navigator.mediaDevices.getUserMedia !== 'function' ||
      pickSupportedMimeType() === null
    ) {
      return 'unsupported'
    }
    return 'supported'
  })

  // Live refs — avoid closure staleness in async handlers. The state
  // setters are used for rendering; the refs are used by
  // listeners/intervals that don't re-attach on every render.
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  // Generation token guarding the async gap inside start(). getUserMedia can
  // take seconds (first-run permission prompt); if the press ends in that gap,
  // stop()/cancel() bump the generation and the in-flight start() aborts when
  // it resumes — otherwise the mic would go hot with NO strip rendered and the
  // 25s cap would auto-SEND ambient shop audio (the "headless hot mic" bug).
  const startGenRef = useRef(0)
  const chunksRef = useRef<Blob[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const meterRafRef = useRef<number | null>(null)
  const durationRafRef = useRef<number | null>(null)
  const startedAtRef = useRef<number>(0)
  const maxLengthTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const stopReasonRef = useRef<StopReason>('user')
  const mimeTypeRef = useRef<string>('')
  // Pending resolver: when stop() is called we set a callback that the
  // recorder.onstop handler invokes once the final chunk has flushed.
  // Prevents a race where the consumer calls stop()+immediately reads
  // state before MediaRecorder finishes its async finalize.
  const stopResolverRef = useRef<(() => void) | null>(null)

  const teardown = useCallback(() => {
    if (meterRafRef.current !== null) {
      cancelAnimationFrame(meterRafRef.current)
      meterRafRef.current = null
    }
    if (durationRafRef.current !== null) {
      cancelAnimationFrame(durationRafRef.current)
      durationRafRef.current = null
    }
    if (maxLengthTimerRef.current !== null) {
      clearTimeout(maxLengthTimerRef.current)
      maxLengthTimerRef.current = null
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try {
          track.stop()
        } catch {
          // ignore
        }
      }
      streamRef.current = null
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close().catch(() => {
        // ignore — some browsers throw on already-closed context
      })
    }
    audioContextRef.current = null
    analyserRef.current = null
    recorderRef.current = null
    chunksRef.current = []
    setLevel(0)
    setDurationMs(0)
  }, [])

  // SPA navigation / unmount cleanup. Without this, the mic indicator
  // can stay on after the user navigates away (privacy nightmare on
  // iOS where the red dot persists in the status bar). The generation
  // bump also aborts a start() still awaiting getUserMedia at unmount.
  useEffect(() => {
    return () => {
      startGenRef.current += 1
      teardown()
    }
  }, [teardown])

  const start = useCallback(async () => {
    if (support === 'unsupported') {
      options.onError?.(new Error('MediaRecorder unsupported in this browser'))
      return
    }
    if (status === 'recording' || status === 'requesting_permission') return

    const mimeType = pickSupportedMimeType()
    if (!mimeType) {
      options.onError?.(new Error('No supported audio MIME type'))
      return
    }

    setStatus('requesting_permission')
    const gen = ++startGenRef.current

    // AudioContext for the level meter MUST be constructed inside the user
    // gesture, BEFORE any await consumes the activation — otherwise iOS
    // Safari hands back a permanently-suspended context (flat level bars).
    const audioCtxCtor =
      (window.AudioContext as typeof AudioContext | undefined) ??
      (
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext
      )
    let ctx: AudioContext | null = null
    if (audioCtxCtor) {
      try {
        ctx = new audioCtxCtor()
        void ctx.resume().catch(() => {})
      } catch {
        ctx = null // meter is non-essential
      }
    }

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          // Browser-level noise suppression + echo cancellation. Whisper
          // copes fine without them, but mechanics will be in a bay
          // with compressors running — the cleaner input matters.
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        },
      })
    } catch (err) {
      ctx?.close().catch(() => {})
      if (gen !== startGenRef.current) return // press already ended
      // Report, then return to 'idle' — the button gates new presses on
      // status === 'idle', so a persistent 'error' would leave the mic
      // permanently inert after the user un-blocks it in browser settings.
      setStatus('idle')
      options.onError?.(
        err instanceof Error ? err : new Error('Permission denied'),
      )
      return
    }
    // The press ended while the permission prompt was up (or a newer press
    // started): release the mic immediately and record nothing.
    if (gen !== startGenRef.current) {
      for (const track of stream.getTracks()) {
        try {
          track.stop()
        } catch {
          // ignore
        }
      }
      ctx?.close().catch(() => {})
      setStatus('idle')
      return
    }
    streamRef.current = stream

    // Detect mid-record permission revocation. `track.onended` fires
    // when the OS / Safari Settings kills the mic without our doing —
    // we treat this exactly like a user cancel (no clip emitted).
    for (const track of stream.getTracks()) {
      track.addEventListener('ended', () => {
        if (recorderRef.current && recorderRef.current.state !== 'inactive') {
          stopReasonRef.current = 'permission_revoked'
          try {
            recorderRef.current.stop()
          } catch {
            // ignore — already stopped
          }
        }
      })
    }

    // Wire the pre-created AudioContext (constructed synchronously in the
    // user gesture above) to the now-available stream for the level meter.
    if (ctx) {
      try {
        audioContextRef.current = ctx
        const source = ctx.createMediaStreamSource(stream)
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        source.connect(analyser)
        analyserRef.current = analyser

        const data = new Uint8Array(analyser.fftSize)
        const tick = () => {
          if (!analyserRef.current) return
          analyserRef.current.getByteTimeDomainData(data)
          // Peak amplitude normalized to [0, 1]. Bytes center at 128
          // (silence); deviation = signal strength.
          let peak = 0
          for (let i = 0; i < data.length; i += 1) {
            const v = Math.abs(data[i] - 128) / 128
            if (v > peak) peak = v
          }
          setLevel(peak)
          meterRafRef.current = requestAnimationFrame(tick)
        }
        meterRafRef.current = requestAnimationFrame(tick)
      } catch {
        // Level meter is non-essential — recording still works without
        // it. Don't fail start() over a meter error.
      }
    }

    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64_000 })
    } catch (err) {
      teardown()
      // 'idle', not 'error' — same un-brick rationale as the getUserMedia
      // rejection path above.
      setStatus('idle')
      options.onError?.(
        err instanceof Error ? err : new Error('MediaRecorder construction failed'),
      )
      return
    }
    recorderRef.current = recorder
    chunksRef.current = []
    mimeTypeRef.current = mimeType
    stopReasonRef.current = 'user'

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data)
      }
    }
    recorder.onerror = (e) => {
      const err = e as Event & { error?: unknown }
      const reason = err.error instanceof Error ? err.error : new Error('MediaRecorder error')
      // A fatal recorder error is followed by dataavailable + stop (spec) —
      // suppress the pending emit or onstop would send the truncated/corrupt
      // partial chunks right after the user saw the error hint.
      stopReasonRef.current = 'cancel'
      options.onError?.(reason)
    }
    recorder.onstop = () => {
      // Compose the final clip. If the stop reason was 'cancel' we
      // intentionally skip onClip — the consumer doesn't want the bytes.
      const reason = stopReasonRef.current
      const durationAtStop = Date.now() - startedAtRef.current
      const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current })
      if (reason !== 'cancel' && blob.size > 0) {
        options.onClip?.({
          blob,
          mimeType: mimeTypeRef.current,
          durationMs: durationAtStop,
          reason,
        })
      }
      teardown()
      setStatus('idle')
      // Resolve any awaiting stop() promise. Unused today but cheap
      // forward-compat if a consumer wants to await stop().
      stopResolverRef.current?.()
      stopResolverRef.current = null
    }

    // Request a chunk every 250ms — keeps the in-memory buffer growing
    // smoothly and gives MediaRecorder a chance to fire ondataavailable
    // before the final flush. Some implementations otherwise emit one
    // giant chunk on stop, which is fine but harder to reason about
    // during debugging.
    try {
      recorder.start(250)
    } catch (err) {
      // start() can throw (permission revoked in the getUserMedia→start gap,
      // NotSupported combos). The recorder is then 'inactive' with NO onstop
      // ever coming — the one state the stop()/cancel() early-returns can't
      // recover — so clean up fully here or the mic stays hot and the button
      // bricks until remount.
      teardown()
      setStatus('idle')
      options.onError?.(
        err instanceof Error ? err : new Error('MediaRecorder start failed'),
      )
      return
    }
    startedAtRef.current = Date.now()
    setStatus('recording')

    // Duration ticker (frame-rate; the meter loop runs at the same
    // cadence so this is essentially free).
    const tickDuration = () => {
      if (!recorderRef.current || recorderRef.current.state === 'inactive') {
        return
      }
      setDurationMs(Date.now() - startedAtRef.current)
      durationRafRef.current = requestAnimationFrame(tickDuration)
    }
    durationRafRef.current = requestAnimationFrame(tickDuration)

    // Hard cap. The server rejects > 30s; we cap at 25s and signal the
    // consumer so VoiceComposer can show a "max length reached" toast.
    maxLengthTimerRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        stopReasonRef.current = 'max_length'
        try {
          recorderRef.current.stop()
        } catch {
          // ignore — race with user stop
        }
      }
    }, MAX_RECORDING_MS)
  }, [options, status, support, teardown])

  const stop = useCallback(() => {
    if (!recorderRef.current) {
      // No recorder yet — the press ended while getUserMedia was pending.
      // Invalidate the in-flight start() so it can't arm a headless mic;
      // there is no audio to emit, so stop degrades to cancel.
      startGenRef.current += 1
      teardown()
      setStatus('idle')
      return
    }
    if (recorderRef.current.state === 'inactive') {
      // Stop already requested — the queued onstop owns emit + cleanup. A
      // second call (double-tap on the send disc, a release racing the
      // max-length cap) must NOT teardown() here: that clears chunksRef and
      // the pending onstop would compose an empty blob, silently dropping
      // the clip.
      return
    }
    stopReasonRef.current = 'user'
    setStatus('stopping')
    try {
      recorderRef.current.stop()
    } catch {
      // ignore — onstop handler still fires
    }
  }, [teardown])

  const cancel = useCallback(() => {
    if (!recorderRef.current) {
      // Also invalidates a start() still awaiting getUserMedia (see stop()).
      startGenRef.current += 1
      teardown()
      setStatus('idle')
      return
    }
    if (recorderRef.current.state === 'inactive') {
      // Stop already requested with onstop still queued (see stop()). Honor
      // the later, explicit discard: flip the pending emit to a cancel and
      // let onstop do the cleanup — never teardown() under it.
      stopReasonRef.current = 'cancel'
      return
    }
    stopReasonRef.current = 'cancel'
    setStatus('stopping')
    try {
      recorderRef.current.stop()
    } catch {
      teardown()
      setStatus('idle')
    }
  }, [teardown])

  return {
    support,
    status,
    level,
    durationMs,
    start,
    stop,
    cancel,
  }
}
